<template>
  <div class="CompletionGauge" :style="containerStyle">
    <svg
      class="CompletionGauge__svg"
      :viewBox="`0 0 ${size} ${svgHeight}`"
      preserveAspectRatio="xMidYMin meet"
      aria-hidden="true"
    >
      <defs>
        <linearGradient :id="gradientId" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stop-color="#d7301f" />
          <stop offset="50%" stop-color="#fee08b" />
          <stop offset="100%" stop-color="#1a9850" />
        </linearGradient>
      </defs>
      <path
        class="CompletionGauge__track"
        :d="arcPath"
        fill="none"
        stroke="#e5e7eb"
        :stroke-width="strokeWidth"
        stroke-linecap="round"
      />
      <path
        class="CompletionGauge__fill"
        :d="arcPath"
        fill="none"
        :stroke="`url(#${gradientId})`"
        :stroke-width="strokeWidth"
        stroke-linecap="round"
        :stroke-dasharray="arcLength"
        :stroke-dashoffset="dashOffset"
      />
      <text
        class="CompletionGauge__value"
        :x="size / 2"
        :y="textY"
        text-anchor="middle"
        dominant-baseline="middle"
      >
        {{ displayText }}
      </text>
    </svg>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    /** Value 0–100 (completion percentage). */
    value: number
    /** Unit suffix (e.g. '%'). */
    unit?: string
    /** Size in pixels (width of SVG viewBox). */
    size?: number
    /** Number of decimal places for value. */
    decimals?: number
  }>(),
  {
    unit: '%',
    size: 120,
    decimals: 0,
  },
)

const gradientId = `completion-gauge-${Math.random().toString(36).slice(2, 11)}`

const strokeWidth = computed(() => Math.max(6, props.size * 0.06))

const radius = computed(() => props.size * 0.4)
const centerX = computed(() => props.size / 2)
const strokePadding = computed(() => Math.ceil(strokeWidth.value / 2) + 2)
const topPadding = computed(() => radius.value * 0.15)
const centerY = computed(() => topPadding.value + radius.value)
const svgHeight = computed(() => centerY.value + strokePadding.value)
const textY = computed(() => centerY.value - radius.value * 0.45)

const arcPath = computed(() => {
  const r = radius.value
  const cx = centerX.value
  const cy = centerY.value
  const startX = cx - r
  const endX = cx + r
  return `M ${startX} ${cy} A ${r} ${r} 0 0 0 ${endX} ${cy}`
})

const arcLength = computed(() => Math.PI * radius.value)

const dashOffset = computed(() => {
  const p = Math.min(100, Math.max(0, props.value)) / 100
  return arcLength.value * (1 - p)
})

const displayText = computed(() => {
  const num = Math.min(100, Math.max(0, props.value))
  const suffix = props.unit ?? ''
  return `${num.toFixed(props.decimals)}${suffix}`
})

const containerStyle = computed(() => ({
  maxWidth: `${props.size}px`,
}))
</script>

<style lang="scss" scoped>
.CompletionGauge {
  display: inline-flex;
  align-items: flex-start;
  justify-content: center;
  margin: 0.5rem 0;
  overflow: visible;
  vertical-align: middle;

  &__svg {
    display: block;
    width: 100%;
    height: auto;
    overflow: visible;
  }

  &__track {
    transition: stroke-dashoffset 0.3s ease-out;
  }

  &__fill {
    transition: stroke-dashoffset 0.3s ease-out;
  }

  &__value {
    font-size: 1.25rem;
    font-weight: 600;
    fill: #1d1d1f;
    font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', Roboto, sans-serif;
  }
}
</style>

<style lang="scss" scoped>
.CompletionGauge {
  display: inline-flex;
  align-items: flex-start;
  justify-content: center;
  margin: 0.5rem 0;
  overflow: visible;
  vertical-align: middle;

  &__svg {
    display: block;
    width: 100%;
    height: auto;
    overflow: visible;
  }

  &__track {
    transition: stroke-dashoffset 0.3s ease-out;
  }

  &__fill {
    transition: stroke-dashoffset 0.3s ease-out;
  }

  &__value {
    font-size: 1.25rem;
    font-weight: 600;
    fill: #1d1d1f;
    font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', Roboto, sans-serif;
  }
}
</style>
